<template>
  <Section
    :id="id"
    :header="t('common.viewParam', { param: t('common.routes') })"
    :subheader="t('maps.maps')"
    class="view-route-section"
    :class="{
      'size-collapsed': size === 'collapsed',
      'size-normal': size === 'normal',
      'size-expanded': size === 'expanded',
    }"
    :aria-describedby="`${id}-subheader`"
  >
    <template #header="{ header, headerId, subheader }">
      <div class="header-container">
        <div class="section-header-left">
          <slot name="toolbar-start" />
          <div class="section-heading-container">
            <h2 :id="headerId" class="visually-hidden" tabindex="-1">
              {{ header }}
            </h2>

            <h3 :id="`${id}-subheader`" class="visually-hidden" tabindex="-1">
              {{ subheader }}
            </h3>

            <RouteSummary
              :proposed-shipments="proposedShipments"
              :mileages="mileages"
            />
          </div>
        </div>

        <div class="section-header-right">
          <RygenButton
            v-if="!poppedOutMapWindow"
            mode="tertiary"
            :label="t('routeBuilder.popOut')"
            icon="pi pi-window-maximize"
            @click="onPopOutMap"
          />
          <SelectButton
            v-model="size"
            :options="sizeOptions"
            option-label="label"
            option-value="key"
            :allow-empty="false"
          />
        </div>
      </div>
    </template>

    <template #default>
      <RouteBuilderMap
        v-if="size !== 'collapsed'"
        ref="map"
        :proposed-shipments="proposedShipments"
        :mileages="mileages"
        :highlighted-shipment-identifiers="highlightedShipmentIdentifiers"
      />
    </template>
  </Section>
</template>

<script lang="ts" setup>
import { PrimeIcons } from '@primevue/core/api';
import { useEventListener } from '@vueuse/core';
import type { PropType, Ref } from 'vue';
import { onBeforeUnmount, ref, toRefs, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import environmentPropertiesStore from '@rygen/rygen-ui/env/EnvironmentPropertiesStore';
import { Optional } from '@rygen/rygen-ui/model';

import { Mileages } from '@/composables/routeBuilder';
import { ProposedShipment } from '@/model/routeBuilder';
import {
  ChildMessage,
  ParentMessage,
} from '@/model/routeBuilder/map/messaging';
import { RoutePlanProposedShipment } from '@/model/routeBuilder/phase2';
import { toRawDeep } from '@/util';

import RouteBuilderMap from '@/components/routeBuilder/RouteBuilderMap.vue';
import RouteSummary from '@/components/routeBuilder/visualizer/RouteSummary.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';
import SelectButton from 'primevue/selectbutton';

import '@vue-flow/core/dist/style.css';

export type ViewRouteSectionSize = 'collapsed' | 'normal' | 'expanded';

export type IntervalHandle = ReturnType<typeof setInterval>;

const MAP_WINDOW_NAME = 'MAP';

const size = defineModel<ViewRouteSectionSize>({
  default: () => 'normal',
});

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  proposedShipments: {
    type: Array as PropType<ProposedShipment[] | RoutePlanProposedShipment[]>,
    default: () => [],
  },
  mileages: {
    type: Object as PropType<Mileages>,
    default: () => ({}),
  },
  highlightedShipmentIdentifiers: {
    type: Array as PropType<Array<RoutePlanProposedShipment['identifier']>>,
    default: () => [],
  },
});
const { id, proposedShipments, mileages, highlightedShipmentIdentifiers } =
  toRefs(props);
const emit = defineEmits<{
  (event: 'collapse'): void;
  (event: 'expand'): void;
  (event: 'reset'): void;
}>();

const map = useTemplateRef('map');

useEventListener('message', ({ data, source }: MessageEvent<ChildMessage>) => {
  if (data.type === 'ready') {
    console.info('child window is ready');

    if (!poppedOutMapWindow.value) {
      size.value = 'collapsed';
      // @ts-ignore We're not using MessagePort or ServiceWorker so `source` will be a Window or Proxy.
      poppedOutMapWindow.value = source;
    }

    if (source) {
      const msg: ParentMessage = {
        type: 'init',
        proposedShipments: toRawDeep(proposedShipments.value),
        mileages: toRawDeep(mileages.value),
        highlightedShipmentIdentifiers: toRawDeep(
          highlightedShipmentIdentifiers.value,
        ),
      };
      source.postMessage(msg);
    }
  } else if (data.type === 'close') {
    if (!!source && 'close' in source) {
      source.close();
    }

    poppedOutMapWindow.value?.close();
    resetPopOutMapState();
  }
});
const { t } = useI18n();

const poppedOutMapWindow: Ref<Optional<WindowProxy>> = ref();
const poppedOutMapWindowCloseInterval: Ref<Optional<IntervalHandle>> = ref();
const sizeOptions = ref([
  {
    key: 'collapsed' as ViewRouteSectionSize,
    icon: PrimeIcons.WINDOW_MINIMIZE,
    label: t('common.collapse'),
    command: () => {
      size.value = 'collapsed';
      emit('collapse');
    },
  },
  {
    key: 'normal' as ViewRouteSectionSize,
    label: t('common.normal'),
    command: () => {
      size.value = 'normal';
      emit('reset');
    },
  },
  {
    key: 'expanded' as ViewRouteSectionSize,
    icon: PrimeIcons.WINDOW_MAXIMIZE,
    label: t('common.expand'),
    command: () => {
      size.value = 'expanded';
      emit('expand');
    },
  },
]);

const onPopOutMap = async () => {
  size.value = 'collapsed';

  const windowFeatures = {
    width: 1200,
    height: 900,
    popup: true,
  } as any;

  // The Screen Management API is Blink only for now so the typescript types
  // won't include it. There's gonna be a lot of ts-ignoring in this block
  // https://developer.mozilla.org/en-US/docs/Web/API/Window_Management_API
  if ('getScreenDetails' in window) {
    const result = await navigator.permissions.query({
      // @ts-ignore
      name: 'window-management',
    });
    if (result.state !== 'denied') {
      // @ts-ignore
      const screenDetails = await window.getScreenDetails();

      // @ts-ignore
      if (window.screen.isExtended) {
        const otherScreens = screenDetails.screens.filter(
          // @ts-ignore
          (s) => s !== screenDetails.currentScreen,
        );

        // @ts-ignore
        const largestScreen = otherScreens.reduce((curr, next) => {
          const currNumPixels = curr.availWidth * curr.availHeight;
          const nextNumPixels = next.availWidth * next.availHeight;

          return currNumPixels > nextNumPixels ? curr : next;
        }, otherScreens[0]);

        if (largestScreen) {
          windowFeatures.width = largestScreen.availWidth;
          windowFeatures.height = largestScreen.availHeight;
          windowFeatures.left = largestScreen.availLeft;
          windowFeatures.top = largestScreen.availTop;
        }
      }
    }
  }

  const windowFeaturesStr = Object.entries(windowFeatures)
    .map(([key, value]) => `${key}=${value}`)
    .join(',');

  const corsairUrl = environmentPropertiesStore.getValue('CORSAIR_URL');
  const newWindow = window.open(
    `${corsairUrl}route-builder/pop-out-map`,
    MAP_WINDOW_NAME,
    windowFeaturesStr,
  );

  if (newWindow) {
    poppedOutMapWindow.value = newWindow;
    poppedOutMapWindow.value.addEventListener('beforeunload', () => {
      console.info('child window closed');
      resetPopOutMapState();
    });

    // Chromium browsers don't fire the `beforeunload` event on the child window
    // so we have to manually check if its been closed
    poppedOutMapWindowCloseInterval.value = window.setInterval(() => {
      if (newWindow.closed) {
        console.info('child window is closed');
        resetPopOutMapState();
      }
    }, 500);
  }
};

const resetPopOutMapState = async () => {
  poppedOutMapWindow.value = undefined;
  window.clearInterval(poppedOutMapWindowCloseInterval.value);
  poppedOutMapWindowCloseInterval.value = undefined;
  size.value = 'normal';
};

watch(size, (newVal: ViewRouteSectionSize) => {
  const sizeOption = sizeOptions.value.find((opt) => opt.key === newVal);
  sizeOption?.command();

  if (map.value) {
    map.value.init();
    map.value.redraw();
  }
});

watch(
  proposedShipments,
  (newVal) => {
    if (poppedOutMapWindow.value) {
      const msg: ParentMessage = {
        type: 'update:shipments',
        proposedShipments: toRawDeep(newVal),
      };
      poppedOutMapWindow.value.postMessage(msg);
    }
  },
  { deep: true },
);

watch(
  mileages,
  (newVal) => {
    if (poppedOutMapWindow.value) {
      const msg: ParentMessage = {
        type: 'update:mileages',
        mileages: toRawDeep(newVal),
      };
      poppedOutMapWindow.value.postMessage(msg);
    }
  },
  { deep: true },
);

watch(
  highlightedShipmentIdentifiers,
  (newVal) => {
    if (poppedOutMapWindow.value) {
      const msg: ParentMessage = {
        type: 'update:highlights',
        highlightedShipmentIdentifiers: toRawDeep(newVal),
      };
      poppedOutMapWindow.value.postMessage(msg);
    }
  },
  { deep: true },
);

onBeforeUnmount(() => {
  poppedOutMapWindow.value?.close();
  if (poppedOutMapWindowCloseInterval.value) {
    window.clearInterval(poppedOutMapWindowCloseInterval.value);
  }
});
</script>

<style>
.view-route-section {
  display: flex;
  flex-flow: column;
  height: 100%;

  &.size-collapsed {
    .section-data,
    .section-footer {
      /* Same as visually-hidden to keep the element in the DOM but hidden */
      position: absolute;
      width: 1px;
      height: 1px;
      margin: -1px;
      border: 0;
      padding: 0;
      white-space: nowrap;
      clip-path: inset(100%);
      clip: rect(0 0 0 0);
      overflow: hidden;
      /* end visually-hidden rules */
    }
  }

  &:not(.size-collapsed) {
    .section-header {
      --section-header-margin: 0 0 var(--sp-3) 0;
    }
  }

  .section-header {
    .header-container {
      display: flex;
      gap: var(--gap-3);
      justify-content: center;
      align-items: center;
      width: 100%;

      .section-header-left,
      .section-header-right {
        flex: 1 0 auto;
      }

      .section-header-right {
        justify-content: flex-end;
      }
    }
  }

  .section-data {
    display: flex;
    flex-flow: column;
    flex: 1 0 auto;
  }

  .section-footer {
    margin-top: var(--sp-6);
  }
}
</style>
